"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.impersonationEndedSchema = exports.impersonationSchema = exports.impersonationInputSchema = exports.breakGlassEntrySchema = exports.breakGlassEntryPathSchema = exports.breakGlassEventSchema = exports.breakGlassInputSchema = exports.platformActionLogSchema = exports.platformActionSchema = exports.ELEVATED_AUDIT_ACTIONS = exports.platformRoleGrantSchema = exports.platformRoleGrantInputSchema = exports.provisionedPracticeSchema = exports.provisionPracticeInputSchema = exports.provisionedTenantSchema = exports.provisionTenantInputSchema = exports.platformTenantListSchema = exports.platformTenantSchema = exports.platformTenantPathSchema = void 0;
// The platform operator's routes, `/api/rpm/v1/platform/**` (`RPM-064`, `CR-46`, `J-297`).
//
// THE ONLY ROUTES THAT ACCEPT A `PLATFORM` ASSIGNMENT. Provisioning a tenant, its practice and its
// first customer administrator, the log of those acts, and break-glass: one resident's entry, read
// only, with a declared reason. Every act carries the operator's reason in its body; nothing here
// names a resident, and no identifier of one travels in a path or a query string other than the
// entry's opaque id (`.claude/rules/phi.md` rule 2).
const zod_1 = require("zod");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
const rpm_entries_js_1 = require("./rpm-entries.js");
/**
 * Why the operator acts. Required on every act and refused when blank (`422 REASON_REQUIRED`): an
 * elevated action without a stated reason is not one the trail can be read for.
 */
const platformReason = zod_1.z
    .string({ error: "State why you are doing this." })
    .max(500)
    .meta({ description: "Why the operator acts; recorded with the act" });
/** The tenant a platform act is about, by its opaque id. No `.meta({ id })`: params are inlined. */
exports.platformTenantPathSchema = zod_1.z.object({ tenantId: zod_1.z.uuid() });
/** One customer on the platform, as the console's Tenant column lists it. */
exports.platformTenantSchema = zod_1.z
    .object({
    tenantId: zod_1.z.uuid(),
    tenantSlug: zod_1.z.string(),
    legalName: zod_1.z.string(),
    status: zod_1.z.enum(rpm_enums_js_1.tenantStatus.enumValues),
    /** The tenant's period start, `YYYY-MM-DD`. */
    validFrom: zod_1.z.string(),
})
    .meta({ id: "PlatformTenant", description: "A customer on the platform" });
exports.platformTenantListSchema = zod_1.z
    .object({ tenants: zod_1.z.array(exports.platformTenantSchema) })
    .meta({ id: "PlatformTenantList", description: "Every tenant on the platform, by name" });
/**
 * One mirror data source the new tenant reads (`J-694`): the value `carease.facilities.data_source`
 * carries for its buildings. It becomes one of the tenant's `source_systems` rows, its code the value
 * itself. One live row per value across all tenants (`uq_source_system_mirror_value`): a value another
 * tenant holds is refused with `409 DATA_SOURCE_HELD`. The operator alone names a tenant's data sources.
 */
const provisionDataSourceSchema = zod_1.z.strictObject({
    mirrorDataSourceValue: zod_1.z
        .string()
        .trim()
        .min(1, { error: "Name at least one data source the tenant reads." })
        .max(20, { error: "A data source is at most 20 characters, as the mirror holds it." }),
    displayName: zod_1.z.string().trim().min(1).max(200),
});
exports.provisionTenantInputSchema = zod_1.z
    .strictObject({
    /** Lower-case letters, digits and hyphens; unique on the platform (`uq_tenant_slug`). */
    tenantSlug: zod_1.z.string().regex(/^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/, {
        error: "A tenant's short name is lower-case letters, digits and hyphens.",
    }),
    legalName: zod_1.z.string().trim().min(1, { error: "Give the tenant its legal name." }).max(200),
    /** At least one; each a value no other tenant's live row holds, named once. */
    dataSources: zod_1.z
        .array(provisionDataSourceSchema)
        .min(1, { error: "Name at least one data source the tenant reads." })
        .refine((sources) => new Set(sources.map((d) => d.mirrorDataSourceValue)).size === sources.length, { error: "Name each data source once." }),
    reason: platformReason,
})
    .meta({
    description: "A new tenant, the mirror data sources it reads, and why it is provisioned",
});
/** The new tenant, and the reference rows written with it so it can reach a processable alert. */
exports.provisionedTenantSchema = exports.platformTenantSchema
    .extend({
    /** One `vital_sign_definitions` row per classified vital of `03-RPM-SCHEMA.md` §10.1. */
    referenceVitalCodes: zod_1.z.array(zod_1.z.enum(rpm_enums_js_1.vitalCode.enumValues)),
    /** One `source_systems` row per data source the operator named (`J-694`). */
    dataSources: zod_1.z.array(zod_1.z.object({
        sourceSystemId: zod_1.z.uuid(),
        sourceSystemCode: zod_1.z.string(),
        displayName: zod_1.z.string(),
        mirrorDataSourceValue: zod_1.z.string(),
    })),
})
    .meta({
    id: "ProvisionedTenant",
    description: "A tenant just provisioned, with its reference rows",
});
exports.provisionPracticeInputSchema = zod_1.z
    .strictObject({
    /** Unique among the tenant's current practices (`uq_practice_code`). */
    practiceCode: zod_1.z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,39}$/, {
        error: "A practice code is letters, digits, hyphens and underscores.",
    }),
    practiceName: zod_1.z.string().trim().min(1, { error: "Give the practice its name." }).max(200),
    reason: platformReason,
})
    .meta({ description: "A practice of the tenant, and why it is provisioned" });
exports.provisionedPracticeSchema = zod_1.z
    .object({
    practiceId: zod_1.z.uuid(),
    tenantId: zod_1.z.uuid(),
    practiceCode: zod_1.z.string(),
    practiceName: zod_1.z.string(),
})
    .meta({ id: "ProvisionedPractice", description: "A practice just provisioned" });
/**
 * The tenant's first customer administrator. The account is named by its email address, which the
 * operator types; the grantor is the session and is never in the body (`ra_no_self_grant`).
 *
 * WHERE NO ACCOUNT HAS THE EMAIL, THE OPERATOR MAKES ONE (`J-695`): `name` and `initialPassword` are
 * then required (`422 NEW_ACCOUNT_NEEDS_NAME_AND_PASSWORD`), and the account is recorded as the new
 * tenant's. Where one has it, it is granted as it is and the two are not used.
 */
exports.platformRoleGrantInputSchema = zod_1.z
    .strictObject({
    email: zod_1.z.email({ error: "Give the administrator's email address." }).max(254),
    name: zod_1.z.string().trim().min(1).max(200).optional(),
    /** The sign-in library's own bounds (`plugins/auth.ts`, `minPasswordLength: 15`). */
    initialPassword: zod_1.z
        .string()
        .min(15, { error: "An initial password is at least 15 characters." })
        .max(128)
        .optional(),
    /** Until — ISO 8601 with an offset, after now. A platform grant is always time-bounded. */
    validTo: zod_1.z.iso.datetime({ offset: true }),
    reason: platformReason,
})
    .meta({ description: "The tenant's customer administrator, until when, and why" });
exports.platformRoleGrantSchema = zod_1.z
    .object({
    roleAssignmentId: zod_1.z.uuid(),
    tenantId: zod_1.z.uuid(),
    userId: zod_1.z.uuid(),
    roleCode: zod_1.z.literal("CUSTOMER_ADMINISTRATOR"),
    scopeKind: zod_1.z.literal("TENANT"),
    validTo: zod_1.z.iso.datetime({ offset: true }),
    /** True when no account had the email and the operator's act made it, as this tenant's. */
    accountMade: zod_1.z.boolean(),
})
    .meta({ id: "PlatformRoleGrant", description: "A customer administrator just granted" });
/** The audit actions that mark an act elevated (`CR-12`): the action value is the mark. */
exports.ELEVATED_AUDIT_ACTIONS = [
    "tenant_provisioned",
    "cross_tenant_action",
    "break_glass_granted",
    "break_glass_used",
    "break_glass_revoked",
];
/** One elevated action, with the operator and the reason, as the console's log lists it. */
exports.platformActionSchema = zod_1.z
    .object({
    auditEventId: zod_1.z.string(),
    occurredAt: zod_1.z.iso.datetime({ offset: true }),
    action: zod_1.z.enum(exports.ELEVATED_AUDIT_ACTIONS),
    outcome: zod_1.z.enum(["success", "denied", "error"]),
    /** The table the act wrote, such as `tenants`, `practices`, `role_assignments`. */
    resourceType: zod_1.z.string(),
    resourceId: zod_1.z.string().nullable(),
    operator: zod_1.z.object({
        userId: zod_1.z.uuid().nullable(),
        name: zod_1.z.string().nullable(),
        email: zod_1.z.string().nullable(),
    }),
    /** The reason the operator gave, or the refusal's code where the act was refused. */
    reason: zod_1.z.string().nullable(),
    /**
     * An impersonation's start or stop, or null for any other act (`J-690`). The start's reason is
     * the operator's own words, so the row no longer says by its reason which of the two it is.
     */
    impersonation: zod_1.z.enum(["STARTED", "STOPPED"]).nullable(),
})
    .meta({ id: "PlatformAction", description: "One elevated action in the platform's log" });
exports.platformActionLogSchema = zod_1.z
    .object({ actions: zod_1.z.array(exports.platformActionSchema) })
    .meta({ id: "PlatformActionLog", description: "Elevated actions, newest first" });
/**
 * A break-glass for one resident's entry (`RPM-064` rows 3 and 4, `NE-08`). The resident is read
 * from the entry on the server; the body never names one. A reason shorter than ten characters is
 * refused with `422 BREAK_GLASS_REASON_REQUIRED` (`bge_reason_substantive`).
 */
exports.breakGlassInputSchema = zod_1.z
    .strictObject({
    /** The entry, by its completion record's opaque id. */
    rpmAlertId: zod_1.z.uuid(),
    declaredReason: zod_1.z.string({ error: "Declare why you need this resident's entry." }).max(2000),
    purposeOfUse: zod_1.z.enum(rpm_enums_js_1.purposeOfUse.enumValues).default("OPERATIONS"),
})
    .meta({ description: "One resident's entry, and the declared reason to read it" });
exports.breakGlassEventSchema = zod_1.z
    .object({
    breakGlassEventId: zod_1.z.uuid(),
    rpmAlertId: zod_1.z.uuid(),
    invokedAt: zod_1.z.iso.datetime({ offset: true }),
    expiresAt: zod_1.z.iso.datetime({ offset: true }),
})
    .meta({ id: "BreakGlassEvent", description: "A break-glass, bounded in time" });
/** No `.meta({ id })`: params are inlined. */
exports.breakGlassEntryPathSchema = zod_1.z.object({
    breakGlassEventId: zod_1.z.uuid(),
    entryId: zod_1.z.uuid(),
});
/**
 * The entry, read only: what Enter vitals reopens, less the store key, the store flag and the time on
 * the entry, which belong to the nurse who works it.
 */
exports.breakGlassEntrySchema = zod_1.z
    .object({
    breakGlassEventId: zod_1.z.uuid(),
    expiresAt: zod_1.z.iso.datetime({ offset: true }),
    readOnly: zod_1.z.literal(true),
    entry: rpm_entries_js_1.openedEntrySchema.omit({ submissionKey: true, storable: true, timeOnEntry: true }),
})
    .meta({ id: "BreakGlassEntry", description: "One resident's entry, read under a break-glass" });
/**
 * Impersonating a tenant's customer administrator (`RPM-064`'s API row, `NE-67`). The account is named
 * by its email address, which the operator types; the operator is the session.
 */
exports.impersonationInputSchema = zod_1.z
    .strictObject({
    userEmail: zod_1.z.email({ error: "Give the administrator's email address." }).max(254),
    reason: platformReason,
})
    .meta({ description: "The tenant administrator to act as, and why" });
/** The impersonation just started: whom the session now acts as, and when it ends by itself. */
exports.impersonationSchema = zod_1.z
    .object({
    impersonating: zod_1.z.object({ userEmail: zod_1.z.string() }),
    expiresAt: zod_1.z.iso.datetime({ offset: true }),
})
    .meta({ id: "Impersonation", description: "An impersonation in progress, bounded in time" });
/** The impersonation just ended; the operator's own session is back. */
exports.impersonationEndedSchema = zod_1.z
    .object({ impersonating: zod_1.z.null() })
    .meta({ id: "ImpersonationEnded", description: "No impersonation in progress" });
//# sourceMappingURL=rpm-platform.js.map